import React, { useMemo, useState } from 'react';
import { Modal, Input, Select, Textarea, Btn } from '../UI';
import { fmt } from '../../utils';
import * as api from '../../api';
import { PRIORITY_OPTIONS, parsePvErrorMessage } from './pvUtils';

const NAVY = '#0b318b';
const CTRL = { fontSize: 11.5, padding: '6px 9px', borderRadius: 7, minHeight: 32, boxSizing: 'border-box' };
const LBL = { fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#475569', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const CELL = { border: '1px solid #cbd5e1', padding: '4px 6px', fontSize: 11 };
const HEAD = { ...CELL, background: '#243b64', color: '#fff', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' };

const Field = ({ label, children }) => (
  <div style={{ minWidth: 0 }}><label style={LBL}>{label}</label>{children}</div>
);

/**
 * Correcting a voucher that is still awaiting review.
 *
 * Only the fields the server whitelists are offered — supplier, currency,
 * payment type and the requisition link are absent on purpose, because a
 * correction must stay a correction rather than becoming a rewrite.
 *
 * Bank details edit a per-voucher override, NOT the supplier master: a wrong
 * account number on one payment must not rewrite the record every other
 * voucher reads from.
 *
 * Only changed fields are sent. The server refuses a no-op edit, so sending
 * everything would turn "I opened the modal and closed it" into a version.
 */
export default function VoucherEditModal({ voucher, onClose, onSaved, toast }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const original = useMemo(() => ({
    narration: voucher.narration ?? '',
    payment_method: voucher.payment_method ?? '',
    priority: voucher.priority ?? 'normal',
    manual_voucher_no: voucher.manual_voucher_no ?? '',
    supplier_bank_name: voucher.supplier_bank_name_override ?? voucher.supplier_bank_name ?? '',
    supplier_account_name: voucher.supplier_account_name_override ?? '',
    supplier_account_number: voucher.supplier_account_number_override ?? voucher.supplier_bank_account ?? '',
    invoice_no: voucher.invoice_no ?? '',
    invoice_date: voucher.invoice_date ?? '',
    delivery_note_no: voucher.delivery_note_no ?? '',
    approval_memo_no: voucher.approval_memo_no ?? '',
    po_number: voucher.po_number ?? '',
    grn_number: voucher.grn_number ?? '',
    less_discount: voucher.less_discount ?? 0,
    less_retention: voucher.less_retention ?? 0,
    other_additions: voucher.other_additions ?? 0,
    withheld_vat: voucher.withheld_vat ?? 0,
    other_deductions: voucher.other_deductions ?? 0,
  }), [voucher]);

  const originalLines = useMemo(
    () => (voucher.items || []).map(it => ({
      item_id: it.item_id,
      description: it.description,
      process_quantity: it.process_quantity ?? 0,
      unit_rate: it.unit_rate ?? 0,
    })), [voucher],
  );

  const [form, setForm] = useState(original);
  const [lines, setLines] = useState(originalLines);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (id, k, v) => setLines(ls => ls.map(l => (l.item_id === id ? { ...l, [k]: v } : l)));

  // Only what actually differs is sent.
  const changed = useMemo(() => {
    const header = {};
    Object.keys(original).forEach(k => {
      if (String(form[k] ?? '') !== String(original[k] ?? '')) header[k] = form[k];
    });
    const lineDiffs = [];
    lines.forEach((l, i) => {
      const o = originalLines[i];
      const d = {};
      if (String(l.process_quantity) !== String(o.process_quantity)) d.process_quantity = Number(l.process_quantity);
      if (String(l.unit_rate) !== String(o.unit_rate)) d.unit_rate = Number(l.unit_rate);
      if (Object.keys(d).length) lineDiffs.push({ item_id: l.item_id, ...d });
    });
    return { header, lines: lineDiffs };
  }, [form, original, lines, originalLines]);

  const changeCount = Object.keys(changed.header).length + changed.lines.length;

  const save = async () => {
    setError(null);
    if (!reason.trim()) { setError({ header: 'A correction reason is required.', bullets: [] }); return; }
    if (changeCount === 0) { setError({ header: 'Nothing has been changed yet.', bullets: [] }); return; }
    setSaving(true);
    try {
      const res = await api.reviewEditVoucher(voucher.payment_voucher_id, {
        reason: reason.trim(), ...changed.header, lines: changed.lines,
      });
      toast.show(res.message, 'success');
      onSaved();
    } catch (e) {
      setError(parsePvErrorMessage(e.message));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Edit Voucher — ${voucher.voucher_no} (currently v${voucher.version || 1})`}
      onClose={onClose}
      width={1040}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', fontSize: 11.5, color: '#92400e', lineHeight: 1.6 }}>
          Each accepted edit saves a new version and records every changed field with its
          original and revised value. Corrections cannot exceed the approved requisition
          quantity or its remaining approved value, and cannot change the supplier, the
          payment type or the requisition link. Approvals already given are kept.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <Field label="Narration">
              <Input value={form.narration} onChange={v => set('narration', v)} style={CTRL} />
            </Field>
          </div>
          <Field label="Payment Method"><Input value={form.payment_method} onChange={v => set('payment_method', v)} style={CTRL} /></Field>
          <Field label="Priority"><Select value={form.priority} onChange={v => set('priority', v)} options={PRIORITY_OPTIONS} style={CTRL} /></Field>

          <Field label="Manual Voucher No."><Input value={form.manual_voucher_no} onChange={v => set('manual_voucher_no', v)} style={CTRL} /></Field>
          <Field label="Supplier Bank"><Input value={form.supplier_bank_name} onChange={v => set('supplier_bank_name', v)} style={CTRL} /></Field>
          <Field label="Account Name"><Input value={form.supplier_account_name} onChange={v => set('supplier_account_name', v)} style={CTRL} /></Field>
          <Field label="Account Number"><Input value={form.supplier_account_number} onChange={v => set('supplier_account_number', v)} style={CTRL} /></Field>

          <Field label="Invoice No."><Input value={form.invoice_no} onChange={v => set('invoice_no', v)} style={CTRL} /></Field>
          <Field label="Delivery Note"><Input value={form.delivery_note_no} onChange={v => set('delivery_note_no', v)} style={CTRL} /></Field>
          <Field label="Purchase Order"><Input value={form.po_number} onChange={v => set('po_number', v)} style={CTRL} /></Field>
          <Field label="GRN"><Input value={form.grn_number} onChange={v => set('grn_number', v)} style={CTRL} /></Field>

          <Field label="Approval Memo"><Input value={form.approval_memo_no} onChange={v => set('approval_memo_no', v)} style={CTRL} /></Field>
          <Field label="Discount"><Input type="number" value={form.less_discount} onChange={v => set('less_discount', v)} style={CTRL} /></Field>
          <Field label="Retention"><Input type="number" value={form.less_retention} onChange={v => set('less_retention', v)} style={CTRL} /></Field>
          <Field label="Other Additions"><Input type="number" value={form.other_additions} onChange={v => set('other_additions', v)} style={CTRL} /></Field>

          <Field label="Withheld VAT"><Input type="number" value={form.withheld_vat} onChange={v => set('withheld_vat', v)} style={CTRL} /></Field>
          <Field label="Other Deductions"><Input type="number" value={form.other_deductions} onChange={v => set('other_deductions', v)} style={CTRL} /></Field>
        </div>

        <div>
          <div style={{ fontSize: 11, fontWeight: 800, color: NAVY, letterSpacing: 0.3, marginBottom: 6 }}>
            LINE-LEVEL CORRECTIONS (WITHIN REQUISITION BALANCE)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
              <thead><tr>
                {['#', 'Item', 'Qty', 'Rate', 'Line Amount'].map(h => (
                  <th key={h} style={{ ...HEAD, textAlign: 'center' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.item_id}>
                    <td style={{ ...CELL, textAlign: 'center', fontWeight: 700 }}>{i + 1}</td>
                    <td style={CELL}>{l.description}</td>
                    <td style={{ ...CELL, padding: 2, width: 110 }}>
                      <Input type="number" value={l.process_quantity} onChange={v => setLine(l.item_id, 'process_quantity', v)} style={{ ...CTRL, textAlign: 'right' }} />
                    </td>
                    <td style={{ ...CELL, padding: 2, width: 120 }}>
                      <Input type="number" value={l.unit_rate} onChange={v => setLine(l.item_id, 'unit_rate', v)} style={{ ...CTRL, textAlign: 'right' }} />
                    </td>
                    <td style={{ ...CELL, textAlign: 'right', fontWeight: 700 }}>
                      {fmt((Number(l.process_quantity) || 0) * (Number(l.unit_rate) || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <label style={LBL}>Edit / Correction Reason <span style={{ color: 'var(--red)' }}>*</span></label>
          <Textarea
            value={reason} onChange={setReason} rows={2}
            placeholder="Describe the correction — this is stored against every changed field."
            style={reason.trim() ? undefined : { border: '1px solid var(--red)' }}
          />
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>{error.header}</div>
            {error.bullets?.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#b91c1c', fontSize: 11.5, lineHeight: 1.7 }}>
                {error.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {changeCount === 0 ? 'No changes yet' : `${changeCount} field${changeCount === 1 ? '' : 's'} changed`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="outline" disabled={saving} onClick={onClose}>Cancel</Btn>
            <Btn loading={saving} disabled={changeCount === 0} onClick={save}>
              Save Version v{(voucher.version || 1) + 1}
            </Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}
